/**
 * Referral content defaults + the completeness rules checked before signing.
 * Mirrors ehr-go/internal/modules/prp/validate.go - the server is authoritative;
 * this list only lets staff see what's missing before they try to sign.
 */
import {
  isPriorityDiagnosis,
  PRP_FREQUENCIES,
  PRP_IMPAIRMENTS,
  PRP_LENGTHS,
  PRP_MIN_IMPAIRMENTS,
  type PrpReferralContent,
} from "@/api/prp.api";

export function emptyReferral(): PrpReferralContent {
  return {
    referral_date: "",
    client: {
      name: "",
      date_of_birth: "",
      ssn_last4: "",
      race: "",
      gender: "",
      marital_status: "",
      address: "",
      city: "",
      state: "",
      zip: "",
      phone: "",
    },
    referral_type: "",
    medicaid_number: "",
    requested_length: "",
    requested_frequency: "",
    impairments: [],
    diagnoses: [{ code: "", description: "", primary: true }],
    diagnosed_by: "",
    diagnosis_date: "",
    medications: [{ name: "", dose: "" }],
    no_medication_reason: "",
    reasons_for_referral: "",
    referring_provider: {
      name: "",
      credentials: "",
      supervisor_name: "",
      agency: "",
      user_id: "",
      supervisor_user_id: "",
      external: false,
      email: "",
      phone: "",
      fax: "",
    },
    guardian: { name: "", phone: "", email: "", address: "" },
  };
}

/** Parse stored content, filling any missing fields with defaults. */
export function parseReferral(raw?: string): PrpReferralContent {
  const base = emptyReferral();
  if (!raw) return base;
  try {
    const parsed = JSON.parse(raw) as Partial<PrpReferralContent>;
    return {
      ...base,
      ...parsed,
      client: { ...base.client, ...(parsed.client ?? {}) },
      referring_provider: { ...base.referring_provider, ...(parsed.referring_provider ?? {}) },
      guardian: { ...base.guardian!, ...(parsed.guardian ?? {}) },
      diagnoses: parsed.diagnoses?.length ? parsed.diagnoses : base.diagnoses,
      medications: parsed.medications?.length ? parsed.medications : base.medications,
      impairments: parsed.impairments ?? [],
    };
  } catch {
    return base;
  }
}

const blank = (s?: string) => !s || s.trim() === "";

/** Loose check matching the server's: something@domain.tld, no spaces. */
export function looksLikeEmail(s?: string): boolean {
  return !!s && /^[^\s@,;<>]+@[^\s@,;<>]+\.[^\s@,;<>]{2,}$/.test(s.trim());
}

export function countImpairments(c: PrpReferralContent): number {
  const valid = new Set(PRP_IMPAIRMENTS.map((i) => i.key));
  return new Set(c.impairments.filter((k) => valid.has(k))).size;
}

/** Everything that would block signing, in form order. */
export function referralProblems(c: PrpReferralContent): string[] {
  const p: string[] = [];
  if (blank(c.client.name) || blank(c.client.date_of_birth)) {
    p.push("Client name and date of birth (refresh from the chart)");
  }
  if (c.referral_type !== "initial" && c.referral_type !== "concurrent") p.push("Choose Initial or Concurrent");
  if (blank(c.medicaid_number)) p.push("Medical Assistance / Medicaid number");
  if (!PRP_LENGTHS.some((l) => l.key === c.requested_length)) p.push("Requested length of service");
  if (!PRP_FREQUENCIES.some((f) => f.key === c.requested_frequency)) p.push("Requested frequency of service");
  const n = countImpairments(c);
  if (n < PRP_MIN_IMPAIRMENTS) p.push(`At least ${PRP_MIN_IMPAIRMENTS} functional impairments (${n} checked)`);
  const coded = c.diagnoses.filter((d) => !blank(d.code));
  if (coded.length === 0) p.push("At least one diagnosis code");
  else if (coded.filter((d) => d.primary).length !== 1) p.push("Exactly one primary diagnosis");
  else if (!c.dx_waiver && !isPriorityDiagnosis(coded.find((d) => d.primary)!.code)) {
    p.push("Primary diagnosis must be on the PRP Priority Population list (or check a waiver)");
  }
  if (blank(c.diagnosed_by)) p.push("Who made the diagnosis");
  if (!c.medications.some((m) => !blank(m.name)) && blank(c.no_medication_reason)) {
    p.push("Medications, or why the client is not on medication");
  }
  if (blank(c.reasons_for_referral)) p.push("Reasons for referral");
  if (blank(c.referring_provider.name) || blank(c.referring_provider.credentials)) {
    p.push("Referring provider name and credentials");
  }
  const rp = c.referring_provider;
  if (rp.external && !looksLikeEmail(rp.email)) {
    p.push("Outside provider's email (for the signing link)");
  } else if (!rp.external && blank(rp.user_id) && !blank(rp.name)) {
    p.push("Pick the referring provider from the staff list, or mark them as outside");
  }
  if (!rp.external && blank(rp.supervisor_user_id) && !blank(rp.supervisor_name)) {
    p.push("Pick the clinical supervisor from the staff list");
  }
  return p;
}
